import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "wasp/client/operations";
import {
  getStudentAnalyticsTrades,
  getStudentEquityBasePnl,
  getStudentAccounts,
  getStudentAccountGroups,
  getStudentStrategies,
  getStudentSessions,
  getMyStudents,
} from "wasp/client/operations";
import StudentLayout from "./StudentLayout";
import AnalyticsGridView from "../trading-journal/AnalyticsGridView";
import useTranslation from "../client/hooks/useTranslation";

export default function StudentAnalyticsPage() {
  const t = useTranslation();
  const { studentId } = useParams<{ studentId: string }>();
  const [analyticsArgs, setAnalyticsArgs] = useState<any>({ excludeInactive: false });
  const [equityBaseArgs, setEquityBaseArgs] = useState<any>(null);

  const { data: students = [], isLoading: loadingStudents } = useQuery(getMyStudents);
  const student = studentId ? students.find((s: any) => s.studentId === studentId) : undefined;
  const permissions = student?.permissions ?? {
    canViewJournal: false,
    canViewDashboard: false,
    canViewAnalytics: false,
    canViewSessions: false,
    canViewPropFirmManager: false,
  };

  const enabled = !!studentId && !loadingStudents && permissions.canViewAnalytics;

  const { data: trades = [], isLoading } = useQuery(
    getStudentAnalyticsTrades,
    { studentId: studentId ?? "", ...analyticsArgs },
    { enabled },
  );
  const { data: equityBasePnl } = useQuery(
    getStudentEquityBasePnl,
    { studentId: studentId ?? "", ...equityBaseArgs },
    { enabled: enabled && !!equityBaseArgs },
  );
  const { data: accounts = [] } = useQuery(
    getStudentAccounts,
    { studentId: studentId ?? "" },
    { enabled },
  );
  const { data: accountGroups = [] } = useQuery(
    getStudentAccountGroups,
    { studentId: studentId ?? "" },
    { enabled },
  );
  const { data: strategies = [] } = useQuery(
    getStudentStrategies,
    { studentId: studentId ?? "" },
    { enabled },
  );
  const { data: sessions = [] } = useQuery(
    getStudentSessions,
    { studentId: studentId ?? "" },
    { enabled: enabled && permissions.canViewSessions },
  );

  if (!studentId) return null;

  if (loadingStudents) {
    return (
      <StudentLayout
        studentId={studentId}
        studentFirstName={null}
        studentLastName={null}
        permissions={permissions}
      >
        <p className="text-sm text-muted-foreground">Chargement...</p>
      </StudentLayout>
    );
  }

  if (!permissions.canViewAnalytics) {
    return (
      <StudentLayout
        studentId={studentId}
        studentFirstName={student?.student?.firstName ?? null}
        studentLastName={student?.student?.lastName ?? null}
        permissions={permissions}
      >
        <p className="text-muted-foreground text-sm">{t.mentor.noPermissionSection}</p>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout
      studentId={studentId}
      studentFirstName={student?.student?.firstName ?? null}
      studentLastName={student?.student?.lastName ?? null}
      studentEmail={student?.student?.email ?? undefined}
      permissions={permissions}
    >
      <AnalyticsGridView
        externalTrades={trades as any[]}
        externalIsLoading={isLoading}
        externalEquityBasePnl={equityBasePnl as number | null | undefined}
        externalAccounts={accounts as any[]}
        externalAccountGroups={accountGroups as any[]}
        externalStrategies={strategies as any[]}
        externalSessions={sessions as any[]}
        readOnly={true}
        onFilterArgsChange={(args) => {
          const { startDate, endDate, accountId, groupId, strategyIds, unassignedOnly, excludeInactive } = args;
          setAnalyticsArgs({ startDate, endDate, accountId, groupId, strategyIds, unassignedOnly, excludeInactive });
        }}
        onEquityBaseArgsChange={(args) => {
          if (args) {
            setEquityBaseArgs({ studentId, ...args });
          } else {
            setEquityBaseArgs(null);
          }
        }}
      />
    </StudentLayout>
  );
}
